import {
  Box,
  VStack,
  HStack,
  Text,
  useColorModeValue,
  Divider,
} from '@chakra-ui/react'
import { DataTableMobileCardProps } from '../types'
import { DataTableActions } from './DataTableActions'

export function DataTableMobileCard<T>({
  row,
  columns,
  actions,
  onRowClick,
  customRender,
}: DataTableMobileCardProps<T>) {
  const cardBg = useColorModeValue('white', 'gray.700')
  const borderColor = useColorModeValue('gray.200', 'gray.600')
  const labelColor = useColorModeValue('gray.600', 'gray.400')

  // If custom render is provided, use it
  if (customRender) {
    return (
      <Box
        bg={cardBg}
        borderRadius="lg"
        border="1px"
        borderColor={borderColor}
        p={4}
        cursor={onRowClick ? 'pointer' : 'default'}
        onClick={() => onRowClick?.(row)}
        _hover={onRowClick ? { borderColor: 'admin.500' } : {}}
      >
        {customRender(row, 0)}
      </Box>
    )
  }

  // Get the value from the row using the column key
  const getValue = (key: keyof T | string): any => {
    if (typeof key === 'string' && key.includes('.')) {
      // Handle nested keys like 'user.name'
      return key.split('.').reduce((obj: any, k: string) => obj?.[k], row)
    }
    return (row as any)[key]
  }

  return (
    <Box
      bg={cardBg}
      borderRadius="lg"
      border="1px"
      borderColor={borderColor}
      p={4}
      cursor={onRowClick ? 'pointer' : 'default'}
      onClick={() => onRowClick?.(row)}
      _hover={onRowClick ? { borderColor: 'admin.500' } : {}}
    >
      <VStack spacing={3} align="stretch">
        {/* Render column data */}
        {columns.map((column, index) => {
          const value = getValue(column.key)
          
          return (
            <Box key={index} minW={0}>
              <HStack justify="space-between" align="flex-start" minW={0}>
                <VStack align="flex-start" spacing={0} flex="1" minW={0}>
                  <Text fontSize="xs" color={labelColor} fontWeight="medium">
                    {column.title}
                  </Text>
                  <Box
                    minW={0}
                    w="full"
                    overflowWrap="anywhere"
                    wordBreak="break-word"
                    sx={{
                      '& .chakra-badge, & .chakra-code, & .chakra-text': {
                        maxWidth: '100%',
                        whiteSpace: 'normal',
                        overflowWrap: 'anywhere',
                        wordBreak: 'break-word',
                      },
                      '& .chakra-stack': {
                        maxWidth: '100%',
                        minWidth: 0,
                      },
                    }}
                  >
                    {column.render ? (
                      column.render(value, row)
                    ) : (
                      <Text fontSize="sm" fontWeight="medium" overflowWrap="anywhere">
                        {value?.toString() || '-'}
                      </Text>
                    )}
                    {column.renderSubText && (
                      <Text fontSize="xs" color={labelColor} overflowWrap="anywhere">
                        {column.renderSubText(row)}
                      </Text>
                    )}
                  </Box>
                </VStack>
              </HStack>
              
              {/* Add divider between fields, except for the last one */}
              {index < columns.length - 1 && <Divider />}
            </Box>
          )
        })}

        {/* Actions at the bottom */}
        {actions && actions.length > 0 && (
          <>
            <Divider />
            <HStack justify="flex-end">
              <DataTableActions row={row} actions={actions} />
            </HStack>
          </>
        )}
      </VStack>
    </Box>
  )
}
